import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { useSelector } from 'react-redux';
import { useSearchParams } from 'react-router-dom';
import useChat from '../hooks/useChat';
import useSpeechRecognition from '../hooks/useSpeechRecognition';
import { ensureINSToken, handleFailedRefresh } from '../../services/interactiveServiceFetch';
import ChatMessage from './ChatMessage';
import ChatInput from './ChatInput';
import ChatEmptyState from './ChatEmptyState';
import PreviewPanel from './PreviewPanel';
import SessionList from './SessionList';
import StreamMonitor from './StreamMonitor';
import AgentActivity from './AgentActivity';
import Toast from './Toast';
import { Translate } from 'react-localize-redux';
import Icon, { IconSizes, IconVariants } from '../../components/foundations/Icon';
import {
    AGENT_STATE,
    CHAT_EVENTS,
    CONTEXT_ENV_TOGGLE_OPTIONS,
    CONTEXT_PROD_MODE,
    FEEDBACK_TOAST_VARIANT,
    MESSAGE_ROLES,
    STEP_STATUS,
    trackChatEvent,
} from '../constants';
import { getSelectedLanguage } from '../../selectors/locale';
import { getAdminModeEnabled } from '../../selectors/user';
import ToggleTabGroup from '../../components/atoms/ToggleTabGroup';
import SessionDebugPanel from './SessionDebugPanel';
import css from './ChatView.css';
import { Button } from '../../components/widgets';

const TRANSLATION_PREFIX = 'STATIC.PAGES.ANALYSIS_CHAT';

const ChatView = ({ readOnly = false, initialPayload, runPath, sessionType, headerActions }) => {
    const messagesEndRef = useRef(null);
    const messagesListRef = useRef(null);
    const observerRef = useRef(null);
    const pendingScrollFrameRef = useRef(0);
    const hasStartedRef = useRef(false);

    const scrollMessagesToBottom = useCallback(() => {
        const list = messagesListRef.current;
        if (!list) return;
        list.scrollTop = list.scrollHeight;
    }, []);

    const [showSessionSidebar, setShowSessionSidebar] = useState(true);
    const [dashboardArtifacts, setDashboardArtifacts] = useState(null);
    const [isPinnedToBottom, setIsPinnedToBottom] = useState(true);
    const isPinnedToBottomRef = useRef(true);
    isPinnedToBottomRef.current = isPinnedToBottom;

    const [authLoading, setAuthLoading] = useState(true);
    const [authError, setAuthError] = useState(false);
    const [selectedContextEnv, setSelectedContextEnv] = useState(CONTEXT_PROD_MODE);
    const [sessionDeletedOpen, setSessionDeletedOpen] = useState(false);
    const [sessionDeleteFailedOpen, setSessionDeleteFailedOpen] = useState(false);
    const [sessionLoadFailedOpen, setSessionLoadFailedOpen] = useState(false);
    const [feedbackToast, setFeedbackToast] = useState(null);
    const [showSessionDebug, setShowSessionDebug] = useState(false);

    useEffect(() => {
        if (!feedbackToast) return undefined;
        const timeoutId = setTimeout(() => setFeedbackToast(null), 5000);
        return () => clearTimeout(timeoutId);
    }, [feedbackToast]);

    const handleFeedbackSubmitted = useCallback(() => setFeedbackToast(FEEDBACK_TOAST_VARIANT.SUCCESS), []);
    const handleFeedbackFailed = useCallback(() => setFeedbackToast(FEEDBACK_TOAST_VARIANT.FAILURE), []);

    const isAdminMode = useSelector(getAdminModeEnabled);

    useEffect(() => {
        ensureINSToken()
            .then(result => {
                if (result.ok) return;
                handleFailedRefresh(result);
                setAuthError(true);
            })
            .finally(() => setAuthLoading(false));
    }, []);

    const {
        messages,
        input,
        setInput,
        handleInputChange,
        handleSubmit,
        isLoading,
        activeRunId,
        error,
        stopError,
        reload,
        stop,
        currentSessionId,
        sessions,
        isLoadingSessions,
        sessionsLoadError,
        loadingSessionId,
        sessionDeletedNotice,
        sessionDeleteError,
        sessionLoadError,
        clearCurrentSession,
        loadSession,
        deleteSession,
        updateSessionTitle,
        fetchSessions,
        streamDescriptors,
        streamingSessionIds,
        handleStreamMessage,
        handleStreamError,
    } = useChat({
        runPath,
        sessionType,
        loadSessionsOnMount: !readOnly && !authLoading,
        contextEnv: isAdminMode ? selectedContextEnv : CONTEXT_PROD_MODE,
    });

    useEffect(() => {
        if (!initialPayload || hasStartedRef.current || authLoading || authError) return;
        hasStartedRef.current = true;
        handleSubmit(null, {
            content: initialPayload.content,
            body: initialPayload.body,
        });
    }, [initialPayload, handleSubmit, authLoading, authError]);

    const [searchParams, setSearchParams] = useSearchParams();
    const urlSessionId = searchParams.get('session');
    const urlSyncInitializedRef = useRef(false);
    const currentSessionIdRef = useRef(currentSessionId);

    const setSessionParam = useCallback(
        sessionId => {
            setSearchParams(
                prev => {
                    const next = new URLSearchParams(prev);
                    if (sessionId) next.set('session', sessionId);
                    else next.delete('session');
                    return next;
                },
                { replace: true }
            );
        },
        [setSearchParams]
    );

    useEffect(() => {
        currentSessionIdRef.current = currentSessionId;
    }, [currentSessionId]);

    // URL → state: on initial mount load the URL session, and react to back/forward navigation thereafter.
    useEffect(() => {
        if (readOnly || authLoading || authError) return;
        const wasInitialized = urlSyncInitializedRef.current;
        urlSyncInitializedRef.current = true;
        if ((urlSessionId || null) === (currentSessionIdRef.current || null)) return;
        if (urlSessionId) {
            loadSession(urlSessionId);
        } else if (wasInitialized) {
            clearCurrentSession();
        }
    }, [urlSessionId, readOnly, authLoading, authError, loadSession, clearCurrentSession]);

    // state → URL: keep the query param in sync when the user picks a session in-app.
    useEffect(() => {
        if (readOnly || !urlSyncInitializedRef.current) return;
        if ((urlSessionId || null) === (currentSessionId || null)) return;
        setSessionParam(currentSessionId);
    }, [currentSessionId, readOnly, setSessionParam, urlSessionId]);

    const prevSessionIdRef = useRef(currentSessionId);

    const hasMessages = messages.length > 0;
    const prevMessagesCountRef = useRef(messages.length);

    const setMessagesEndNode = useCallback(node => {
        messagesEndRef.current = node;
        if (observerRef.current) {
            observerRef.current.disconnect();
            observerRef.current = null;
        }
        if (!node) return;
        const observer = new IntersectionObserver(
            ([entry]) => setIsPinnedToBottom(entry.isIntersecting),
            {
                root: messagesListRef.current,
                rootMargin: '0px 0px 64px 0px',
                threshold: 0,
            }
        );
        observer.observe(node);
        observerRef.current = observer;
    }, []);

    useEffect(() => () => cancelAnimationFrame(pendingScrollFrameRef.current), []);

    const [completedRunCount, setCompletedRunCount] = useState(0);
    const prevIsLoadingRef = useRef(isLoading);
    useEffect(() => {
        if (prevIsLoadingRef.current && !isLoading) setCompletedRunCount(c => c + 1);
        prevIsLoadingRef.current = isLoading;
    }, [isLoading]);

    const handledDeletedNonceRef = useRef(0);
    useEffect(() => {
        const nonce = sessionDeletedNotice?.nonce ?? 0;
        if (!nonce || nonce === handledDeletedNonceRef.current) return;
        handledDeletedNonceRef.current = nonce;
        setSessionDeletedOpen(true);
        if (sessionDeletedNotice.sessionId && sessionDeletedNotice.sessionId === urlSessionId) {
            setSessionParam(null);
        }
    }, [sessionDeletedNotice, urlSessionId, setSessionParam]);

    useEffect(() => {
        if (!sessionDeletedOpen) return;
        const timeoutId = setTimeout(() => setSessionDeletedOpen(false), 5000);
        return () => clearTimeout(timeoutId);
    }, [sessionDeletedOpen, sessionDeletedNotice?.nonce]);

    const handledDeleteFailedNonceRef = useRef(0);
    useEffect(() => {
        const nonce = sessionDeleteError?.nonce ?? 0;
        if (!nonce || nonce === handledDeleteFailedNonceRef.current) return;
        handledDeleteFailedNonceRef.current = nonce;
        setSessionDeleteFailedOpen(true);
    }, [sessionDeleteError]);

    useEffect(() => {
        if (!sessionDeleteFailedOpen) return;
        const timeoutId = setTimeout(() => setSessionDeleteFailedOpen(false), 5000);
        return () => clearTimeout(timeoutId);
    }, [sessionDeleteFailedOpen, sessionDeleteError?.nonce]);

    const handledLoadFailedNonceRef = useRef(0);
    useEffect(() => {
        const nonce = sessionLoadError?.nonce ?? 0;
        if (!nonce || nonce === handledLoadFailedNonceRef.current) return;
        handledLoadFailedNonceRef.current = nonce;
        setSessionLoadFailedOpen(true);
        if (sessionLoadError.sessionId && sessionLoadError.sessionId === urlSessionId) {
            setSessionParam(null);
        }
    }, [sessionLoadError, urlSessionId, setSessionParam]);

    useEffect(() => {
        if (!sessionLoadFailedOpen) return;
        const timeoutId = setTimeout(() => setSessionLoadFailedOpen(false), 5000);
        return () => clearTimeout(timeoutId);
    }, [sessionLoadFailedOpen, sessionLoadError?.nonce]);

    const activeLanguage = useSelector(getSelectedLanguage);

    const {
        isListening: isMicListening,
        isSupported: isMicSupported,
        toggle: toggleMic,
        stop: stopMic,
        syncBaseText: syncMicBaseText,
    } = useSpeechRecognition({ onTranscript: setInput, lang: activeLanguage });

    const onMicClick = useCallback(() => {
        if (isMicListening) {
            trackChatEvent(CHAT_EVENTS.MICROPHONE_STOP, {
                sessionId: currentSessionId,
                hasTranscript: !!input.trim(),
            });
        } else {
            trackChatEvent(CHAT_EVENTS.MICROPHONE_START, { sessionId: currentSessionId });
        }
        toggleMic(input);
    }, [toggleMic, input, isMicListening, currentSessionId]);

    const handleStop = useCallback(() => {
        trackChatEvent(CHAT_EVENTS.STOP_GENERATION, { sessionId: currentSessionId, runId: activeRunId });
        stop();
    }, [stop, currentSessionId, activeRunId]);

    const handleRegenerate = useCallback(() => {
        trackChatEvent(CHAT_EVENTS.REGENERATE_RESPONSE, { sessionId: currentSessionId });
        reload();
    }, [reload, currentSessionId]);

    useEffect(() => {
        if (isLoading && isMicListening) stopMic();
    }, [isLoading, isMicListening, stopMic]);

    useEffect(() => {
        const sessionChanged = prevSessionIdRef.current !== currentSessionId;
        prevSessionIdRef.current = currentSessionId;

        if (sessionChanged) {
            prevMessagesCountRef.current = messages.length;
            scrollMessagesToBottom();
            setIsPinnedToBottom(true);
            if (isMicListening) stopMic();
            return;
        }

        const grew = messages.length > prevMessagesCountRef.current;
        prevMessagesCountRef.current = messages.length;

        if (grew && messages[messages.length - 1]?.role === MESSAGE_ROLES.USER) {
            setIsPinnedToBottom(true);
            scrollMessagesToBottom();
            return;
        }

        if (!isPinnedToBottomRef.current) return;
        if (pendingScrollFrameRef.current) return;
        pendingScrollFrameRef.current = requestAnimationFrame(() => {
            pendingScrollFrameRef.current = 0;
            scrollMessagesToBottom();
        });
    }, [messages, currentSessionId, isMicListening, stopMic, scrollMessagesToBottom]);

    const submitWithMicOff = useCallback((e, options) => {
        const usedVoice = isMicListening;
        if (isMicListening) stopMic();
        const content = (options?.content ?? input) ?? '';
        const messageLength = content.trim().length;
        if (messageLength) {
            trackChatEvent(CHAT_EVENTS.SEND_MESSAGE, {
                sessionId: currentSessionId,
                isNewSession: !currentSessionId,
                messageLength,
                usedVoice,
            });
        }
        return handleSubmit(e, options);
    }, [handleSubmit, isMicListening, stopMic, input, currentSessionId]);

    useEffect(() => {
        if (isLoading) return;
        syncMicBaseText(input);
    }, [input, syncMicBaseText, isLoading]);

    const lastAssistantMessage = useMemo(
        () => messages.findLast(msg => msg.role === MESSAGE_ROLES.ASSISTANT) || null,
        [messages]
    );

    const agentActivity = useMemo(() => {
        if (!lastAssistantMessage) return null;
        const { steps = [], startedAt, finishedAt } = lastAssistantMessage;
        const finishedSteps = steps.filter(
            s => s.status === STEP_STATUS.COMPLETED || s.status === STEP_STATUS.FAILED
        );
        const runningStep = steps.find(s => s.status === STEP_STATUS.RUNNING);

        if (!isLoading) {
            if (finishedSteps.length === 0) return null;
            const totalMs = startedAt && finishedAt ? finishedAt - startedAt : 0;
            return {
                state: AGENT_STATE.DONE,
                currentTool: null,
                history: finishedSteps,
                totalDurationMs: totalMs,
            };
        }

        // A finished prior-turn message means the new run hasn't created its assistant
        // message yet; don't surface that turn's steps under the new run.
        if (finishedAt) {
            return {
                state: AGENT_STATE.THINKING,
                currentTool: null,
                history: [],
                totalDurationMs: 0,
            };
        }

        if (runningStep) {
            return {
                state: AGENT_STATE.RUNNING,
                currentTool: { name: runningStep.title || 'Working' },
                history: finishedSteps,
                totalDurationMs: 0,
            };
        }

        return {
            state: AGENT_STATE.THINKING,
            currentTool: null,
            history: finishedSteps,
            totalDurationMs: 0,
        };
    }, [lastAssistantMessage, isLoading]);

    const lastMessageDashboardArtifacts = useMemo(() => {
        const lastMessage = messages?.[messages.length - 1];
        if (!lastMessage || lastMessage.role !== MESSAGE_ROLES.ASSISTANT) return null;
        return lastMessage.content?.filter(block => block.is_final_analysis);
    }, [messages]);

    const onDashboardClick = useCallback(artifacts => setDashboardArtifacts(artifacts), []);

    const scrollToBottom = useCallback(() => {
        scrollMessagesToBottom();
        setIsPinnedToBottom(true);
    }, [scrollMessagesToBottom]);

    const previewArtifacts = dashboardArtifacts?.length > 0 ? dashboardArtifacts : lastMessageDashboardArtifacts;

    if (authLoading && !initialPayload) {
        return (
            <div className={css.chatView}>
                <div className={css.loading}>
                    <Icon name="loader" size={IconSizes.LARGE} className={css.spinningLoader} />
                    <div className={css.loadingText}><Translate id={`${TRANSLATION_PREFIX}.AUTH_LOADING`} /></div>
                </div>
            </div>
        );
    }

    if (authError) {
        return (
            <div className={css.chatView}>
                <div className={css.authError}>
                    <Icon name="alert-triangle" size={IconSizes.LARGE} className={css.authErrorIcon} />
                    <div className={css.authErrorText}><Translate id={`${TRANSLATION_PREFIX}.AUTH_ERROR`} /></div>
                </div>
            </div>
        );
    }

    const onNewChat = () => {
        trackChatEvent(CHAT_EVENTS.NEW_CHAT, { previousSessionId: currentSessionId });
        clearCurrentSession();
        setDashboardArtifacts(null);
    };

    const toggleSessionSidebar = () => {
        trackChatEvent(CHAT_EVENTS.TOGGLE_SESSION_SIDEBAR, { isOpen: !showSessionSidebar });
        setShowSessionSidebar(!showSessionSidebar);
    };

    const onSessionDelete = async sessionId => {
        const isSessionStreaming = streamingSessionIds.includes(sessionId);
        if (isSessionStreaming) {
            try {
                await stop();
            } catch (err) {
                console.error('Failed to cancel run before delete:', err);
            }
        }
        return await deleteSession(sessionId);
    };

    const onSessionSelect = async sessionId => {
        if (loadingSessionId) return;
        if (sessionId !== currentSessionId) {
            trackChatEvent(CHAT_EVENTS.OPEN_CHAT, { sessionId });
            setDashboardArtifacts(null);
            await loadSession(sessionId);
        }
    };

    const onSessionUpdate = async (sessionId, updates) => {
        if (updates.title) return await updateSessionTitle(sessionId, updates.title);
        return true;
    };

    return (
        <div className={css.chatView}>
            {streamDescriptors.map(({ key, runId }) => (
                <StreamMonitor
                    key={runId}
                    sessionKey={key}
                    runId={runId}
                    onMessage={handleStreamMessage}
                    onError={handleStreamError}
                />
            ))}
            {!readOnly && (
                <div className={css.header}>
                    <div className={css.title}>
                        <Translate id={`${TRANSLATION_PREFIX}.TITLE`} />
                    </div>
                    <div className={css.headerActions}>
                        {headerActions}
                        {isAdminMode && (
                            <ToggleTabGroup
                                options={CONTEXT_ENV_TOGGLE_OPTIONS}
                                selectedOption={selectedContextEnv}
                                onToggle={setSelectedContextEnv}
                            />
                        )}
                        {isAdminMode && (
                            <Icon
                                name="bug"
                                size={IconSizes.MEDIUM}
                                variant={showSessionDebug ? IconVariants.ACTIVE : IconVariants.DARK}
                                onClick={() => setShowSessionDebug(open => !open)}
                                hoverText={`${TRANSLATION_PREFIX}.SESSION_DEBUG_HOVER`}
                            />
                        )}
                        <Button Icon="plus" onClick={onNewChat} disabled={!hasMessages}>
                            <Translate id={`${TRANSLATION_PREFIX}.NEW_CHAT`} />
                        </Button>
                    </div>
                </div>
            )}
            <div className={css.body}>
                {sessionDeletedOpen && (
                    <Toast onClose={() => setSessionDeletedOpen(false)}>
                        <Translate id={`${TRANSLATION_PREFIX}.SESSION_DELETED_TOAST`} />
                    </Toast>
                )}
                {sessionDeleteFailedOpen && (
                    <Toast icon="alert-triangle" onClose={() => setSessionDeleteFailedOpen(false)}>
                        <Translate id={`${TRANSLATION_PREFIX}.SESSION_DELETE_FAILED_TOAST`} />
                    </Toast>
                )}
                {sessionLoadFailedOpen && (
                    <Toast icon="alert-triangle" onClose={() => setSessionLoadFailedOpen(false)}>
                        <Translate id={`${TRANSLATION_PREFIX}.SESSION_LOAD_FAILED_TOAST`} />
                    </Toast>
                )}
                {feedbackToast && (
                    <Toast
                        icon={feedbackToast === FEEDBACK_TOAST_VARIANT.SUCCESS ? 'check' : 'alert-triangle'}
                        onClose={() => setFeedbackToast(null)}
                    >
                        <Translate
                            id={`${TRANSLATION_PREFIX}.FEEDBACK.${
                                feedbackToast === FEEDBACK_TOAST_VARIANT.SUCCESS ? 'SUBMITTED_TOAST' : 'FAILED_TOAST'
                            }`}
                        />
                    </Toast>
                )}
                {!readOnly && (
                    <>
                        <div className={classNames(css.sessionSidebar, { [css.collapsed]: !showSessionSidebar })}>
                            <SessionList
                                sessions={sessions}
                                currentSessionId={currentSessionId}
                                loadingSessionId={loadingSessionId}
                                streamingSessionIds={streamingSessionIds}
                                onSessionSelect={onSessionSelect}
                                onSessionDelete={onSessionDelete}
                                onSessionUpdate={onSessionUpdate}
                                isLoading={isLoadingSessions}
                                loadError={sessionsLoadError}
                                onRetry={fetchSessions}
                            />
                        </div>
                        <Icon
                            name={showSessionSidebar ? 'chevron-left' : 'chevron-right'}
                            size={IconSizes.MEDIUM}
                            className={classNames(css.sidebarButton, { [css.collapsed]: !showSessionSidebar })}
                            onClick={toggleSessionSidebar}
                            hoverText={`${TRANSLATION_PREFIX}.${showSessionSidebar ? 'HIDE_SIDEBAR_HOVER' : 'SHOW_SIDEBAR_HOVER'}`}
                        />
                    </>
                )}
                <div className={css.content}>
                    <div className={css.messagesContainer}>
                        {!readOnly && !hasMessages && (
                            <ChatEmptyState
                                onSuggestionClick={query => submitWithMicOff(null, { content: query })}
                                input={input}
                                handleInputChange={handleInputChange}
                                handleSubmit={submitWithMicOff}
                                isLoading={isLoading}
                                stop={handleStop}
                                onMicClick={onMicClick}
                                isMicListening={isMicListening}
                                micSupported={isMicSupported}
                            />
                        )}
                        {hasMessages && (
                            <div ref={messagesListRef} className={css.messagesList}>
                                {messages.map(message => (
                                    <ChatMessage
                                        key={message.id}
                                        message={message}
                                        sessionId={currentSessionId}
                                        onDashboardClick={onDashboardClick}
                                        onFeedbackSubmitted={handleFeedbackSubmitted}
                                        onFeedbackFailed={handleFeedbackFailed}
                                    />
                                ))}
                                <div ref={setMessagesEndNode} />
                            </div>
                        )}
                        {hasMessages && !isPinnedToBottom && !error && !stopError && (
                            <button
                                type="button"
                                className={css.jumpToBottom}
                                onClick={scrollToBottom}
                                aria-label="Scroll to latest message"
                            >
                                <Icon name="chevron-down" size={IconSizes.SMALL} interactive={false} />
                            </button>
                        )}
                        {(error || stopError) && (
                            <div className={css.chatError}>
                                <Icon
                                    name="alert-triangle"
                                    variant={IconVariants.ERROR}
                                    size={IconSizes.SMALL}
                                    className={css.chatErrorIcon}
                                    interactive={false}
                                />
                                <span className={css.chatErrorMessage}>{error || stopError}</span>
                                {error && (
                                    <button onClick={handleRegenerate} className={css.chatErrorRetry}>
                                        <Translate id={`${TRANSLATION_PREFIX}.RETRY`} />
                                    </button>
                                )}
                            </div>
                        )}
                    </div>
                    {isAdminMode && !readOnly && showSessionDebug && (
                        <SessionDebugPanel
                            sessionId={currentSessionId}
                            refreshKey={completedRunCount}
                            onClose={() => setShowSessionDebug(false)}
                        />
                    )}
                    {hasMessages && (agentActivity || !readOnly) && (
                        <div className={css.bottomDock}>
                            {agentActivity && (
                                <AgentActivity
                                    state={agentActivity.state}
                                    currentTool={agentActivity.currentTool}
                                    history={agentActivity.history}
                                    totalDurationMs={agentActivity.totalDurationMs}
                                />
                            )}
                            {!readOnly && (
                                <ChatInput
                                    input={input}
                                    handleInputChange={handleInputChange}
                                    handleSubmit={submitWithMicOff}
                                    isLoading={isLoading}
                                    stop={handleStop}
                                    onMicClick={onMicClick}
                                    isMicListening={isMicListening}
                                    micSupported={isMicSupported}
                                />
                            )}
                        </div>
                    )}
                </div>
                {previewArtifacts?.length > 0 && <PreviewPanel key={currentSessionId} artifacts={previewArtifacts} />}
            </div>
        </div>
    );
};

ChatView.propTypes = {
    readOnly: PropTypes.bool,
    initialPayload: PropTypes.shape({
        content: PropTypes.string.isRequired,
        body: PropTypes.object.isRequired,
    }),
    runPath: PropTypes.string.isRequired,
    sessionType: PropTypes.string.isRequired,
    headerActions: PropTypes.node,
};

export default ChatView;
